import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useIntl } from 'react-intl';

import { Patient } from '@/shared/api/protocol-ts/model/dto_patient_pb';
import { PatientsListColumnName } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { parseDateAsLocal } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { StudiesCounters } from '@/entities/patient';

import { i18n, PatientListColumnVisibility } from '../config';
import { PatientDoctorsCell } from '../ui/PatientDoctorsCell/PatientDoctorsCell';
import styles from '../ui/PatientList/PatientList.module.scss';
import { PatientNameCell } from '../ui/PatientNameCell/PatientNameCell';

interface ColumnMeta {
  isVisible?: boolean;
}

export const useGetPatientsColumnsScheme = (
  columnVisibility: PatientListColumnVisibility,
) => {
  const { formatMessage, formatDate } = useIntl();

  const columnHelper = createColumnHelper<Patient>();

  const { isFDA } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const isShown = (column: PatientsListColumnName) =>
    columnVisibility[column] !== false;

  const columnsScheme = [
    columnHelper.accessor((row) => row, {
      id: PatientsListColumnName.PatientName,
      enableSorting: true,
      minSize: 180,
      header: formatMessage(
        i18n.ColumnValues[PatientsListColumnName.PatientName],
      ),
      cell: (cell) => <PatientNameCell patient={cell.getValue()} />,
    }),
    columnHelper.accessor((row) => row.ExternalID, {
      id: PatientsListColumnName.PatientID,
      meta: {
        isVisible: isShown(PatientsListColumnName.PatientID),
      } as ColumnMeta,
      enableSorting: true,
      minSize: 120,
      header: formatMessage(
        i18n.ColumnValues[PatientsListColumnName.PatientID],
      ),
      cell: (cell) => (
        <div className={cn(styles.infoInCell, styles.greyText)}>
          <span data-ph-mask className={styles.forWhiteSpace}>
            {cell.getValue()}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.PersonalData?.DateOfBirth, {
      id: PatientsListColumnName.DateOfBirth,
      meta: {
        isVisible: isShown(PatientsListColumnName.DateOfBirth),
      } as ColumnMeta,
      enableSorting: false,
      minSize: 180,
      header: formatMessage(
        i18n.ColumnValues[PatientsListColumnName.DateOfBirth],
      ),
      cell: (cell) => {
        const dateOfBirth = cell.getValue();
        const formattedDate = dateOfBirth
          ? formatDate(parseDateAsLocal(dateOfBirth), {
              dateStyle: 'long',
            })
          : '';

        return (
          <div className={cn(styles.infoInCell, styles.greyText)}>
            <span data-ph-mask className={styles.forWhiteSpace}>
              {formattedDate}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.ID, {
      id: PatientsListColumnName.Doctors,
      meta: {
        isVisible: isShown(PatientsListColumnName.Doctors),
      } as ColumnMeta,
      enableSorting: false,
      minSize: 160,
      header: formatMessage(i18n.ColumnValues[PatientsListColumnName.Doctors]),
      cell: (cell) => {
        const patientID = cell.getValue();

        return <PatientDoctorsCell patientID={patientID} />;
      },
    }),
    columnHelper.accessor((row) => row.Revision?.UpdatedAt, {
      id: PatientsListColumnName.LastModified,
      meta: {
        isVisible: isShown(PatientsListColumnName.LastModified),
      } as ColumnMeta,
      enableSorting: true,
      minSize: 260,
      header: formatMessage(
        i18n.ColumnValues[PatientsListColumnName.LastModified],
      ),
      cell: (cell) => {
        const timestamp = cell.getValue();

        if (!timestamp) {
          return null;
        }

        const formattedDate = formatDate(timestamp.toDate(), {
          dateStyle: 'long',
        });

        return (
          <div className={cn(styles.infoInCell, styles.greyText)}>
            <span className={styles.forWhiteSpace}>{formattedDate}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.ID, {
      id: PatientsListColumnName.Studies,
      meta: {
        isVisible: !isFDA && isShown(PatientsListColumnName.Studies),
      } as ColumnMeta,
      enableSorting: false,
      minSize: 300,
      header: formatMessage(i18n.ColumnValues[PatientsListColumnName.Studies]),
      cell: (cell) => {
        const patientID = cell.getValue();

        return (
          <div>
            <StudiesCounters patientID={patientID} />
          </div>
        );
      },
    }),
  ].filter(
    (column): column is typeof column & { meta: ColumnMeta } =>
      (column.meta as ColumnMeta | undefined)?.isVisible !== false,
  );

  return columnsScheme;
};
